import React from "react";
import { Image, message } from "antd";
import { BiPlus } from "react-icons/bi";
import { FiEdit2, FiSave } from "react-icons/fi";
import { IoClose, IoTrash } from "react-icons/io5";
import ImageUploader from "../ImageUploader";
import { formatDateTime } from "../../../../utils/helpers";
import { getImg } from "../../../../Api/getImg";
import drEasyApi from "../../../../Api/Customapi";

export default function GeneralNotesTab({
  reportId,
  freeFormNotes,
  setFreeFormNotes,
  savedSections,
  setSavedSections,
  newSection,
  setNewSection,
  editingSectionId,
  setEditingSectionId,
  savingNotes,
  setSavingNotes,
  savingSection,
  setSavingSection,
  seeMore,
  setSeeMore,
  onSaveNotes,
  // ── pagination ──
  reportCount,
  currentReportIndex,
  onReportIndexChange,
  fetchUserReport
}) {
  const openAddSectionForm = () => {
    setNewSection({ title: "", content: "", photoIDs: [] });
    setEditingSectionId(null);
  };

  const cancelNewSection = () => setNewSection(null);

  const updateNewSection = (field, value) =>
    setNewSection((prev) => ({ ...prev, [field]: value }));

  const handleEditReport = async () => {
    setSavingNotes(true);
    try {
      await onSaveNotes();
            await fetchUserReport?.();
      message.success("General notes saved successfully");
    } catch {
      message.error("Failed to save general notes");
    } finally {
      setSavingNotes(false);
    }
  };

  const handleAddSection = async () => {
    if (!newSection.title.trim() || !newSection.content.trim()) {
      message.error("Please fill in title and content");
      return;
    }
    setSavingSection(true);
    try {
      const response = await drEasyApi.post("/api/Reports/AddSection", {
        reportId,
        title: newSection.title,
        body: newSection.content,
        photoIDs: newSection.photoIDs,
      });
      setSavedSections((prev) => [
        {
          sectionId: response.data.id || response.data,
          title:     response.data.title  || newSection.title,
          content:   response.data.body   || newSection.content,
          photoIDs:  response.data.photoIds || newSection.photoIDs,
          createdAt: response.data.createdAt || new Date().toISOString(),
        },
        ...prev,
      ]);
                  await fetchUserReport?.();

      message.success("Section added successfully");
      setNewSection(null);
    } catch {
      message.error("Failed to add section");
    } finally {
      setSavingSection(false);
    }
  };

  const startEditSection  = (sectionId) => { setEditingSectionId(sectionId); setNewSection(null); };
  const cancelEditSection = () => setEditingSectionId(null);

  const updateEditingSection = (sectionId, field, value) => {
    setSavedSections((prev) =>
      prev.map((s) => (s.sectionId === sectionId ? { ...s, [field]: value } : s)),
    );
  };

  const handleEditSection = async (section) => {
    if (!section.title.trim() || !section.content.trim()) {
      message.error("Please fill in title and content");
      return;
    }
    setSavingSection(true);
    try {
      await drEasyApi.put("/api/Reports/EditSection", {
        sectionId: section.sectionId,
        title:     section.title,
        body:      section.content,
        photoIDs:  section.photoIDs,
      });
      message.success("Section updated successfully");
      setEditingSectionId(null);
    } catch (err) {
      message.error(
        `Failed to update section: ${err.response?.data?.message || err.message || "Unknown error"}`,
      );
    } finally {
      setSavingSection(false);
    }
  };

  const handleDeleteSection = async (sectionId) => {
    try {
      await drEasyApi.delete(`/api/Reports/DeleteSection/${sectionId}`);
      setSavedSections((prev) => prev.filter((s) => s.sectionId !== sectionId));
      message.success("Section deleted successfully");
    } catch {
      message.error("Failed to delete section");
    }
  };

  return (
    <div>

      {/* ── General Notes header row: pagination (left) + save (right) ── */}
      <div className="flex items-center justify-between gap-4 mb-3">

        {/* Pagination */}
        {reportCount > 1 ? (
          <div className="flex items-center gap-3 py-2 px-4 bg-primary/5 border border-primary/20 rounded-lg">
            <button
              onClick={() => onReportIndexChange(currentReportIndex - 1)}
              disabled={currentReportIndex === 0}
              className="px-3 py-1 rounded border border-primary text-primary font-semibold text-sm hover:bg-primary hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              ← Prev
            </button>
            <span className="text-sm font-semibold text-gray-700 whitespace-nowrap">
              Report {currentReportIndex + 1} of {reportCount}
            </span>
            <button
              onClick={() => onReportIndexChange(currentReportIndex + 1)}
              disabled={currentReportIndex === reportCount - 1}
              className="px-3 py-1 rounded border border-primary text-primary font-semibold text-sm hover:bg-primary hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              Next →
            </button>
          </div>
        ) : (
          <div /> // spacer keeps Save right-aligned when no pagination
        )}

        {/* Save Notes button */}
        <button
          onClick={handleEditReport}
          disabled={savingNotes}
          className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <FiEdit2 size={18} />
          {savingNotes ? "Saving..." : "Save Notes"}
        </button>
      </div>

      {/* Free-form notes */}
      <div className="mb-6">
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          General Notes
        </label>
        <textarea
          value={freeFormNotes}
          onChange={(e) => setFreeFormNotes(e.target.value)}
          placeholder="Write your general observations and notes here..."
          className="w-full min-h-[200px] p-4 border-2 border-primary/30 rounded-lg focus:outline-none focus:border-primary/90 focus:ring-2 focus:ring-primary transition-all resize-y"
          style={{ fontFamily: "monospace" }}
        />
      </div>

      {/* Add section button */}
      <div className="mb-6">
        <button
          onClick={openAddSectionForm}
          disabled={newSection !== null}
          className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <BiPlus size={20} />
          Add Section
        </button>
      </div>

      {/* New section form */}
      {newSection && (
        <div className="border-2 border-primary/40 rounded-lg p-4 bg-primary/10 mb-4">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">New Section</h3>
          <input
            type="text"
            value={newSection.title}
            onChange={(e) => updateNewSection("title", e.target.value)}
            placeholder="Section Title (e.g., Diagnosis, Treatment Plan)"
            className="w-full px-3 py-2 border-2 border-primary/30 rounded-lg focus:outline-none focus:border-primary/90 font-semibold mb-3"
          />
          <textarea
            value={newSection.content}
            onChange={(e) => updateNewSection("content", e.target.value)}
            placeholder="Enter section content..."
            className="w-full min-h-[120px] p-3 border-2 border-primary/30 rounded-lg focus:outline-none focus:border-primary/90 focus:ring-2 focus:ring-primary transition-all resize-y mb-3"
          />
          <div className="mb-3">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Section Images
            </label>
            <ImageUploader
              value={newSection.photoIDs}
              onChange={(photoIDs) => updateNewSection("photoIDs", photoIDs)}
              multiple={true}
              spec={true}
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleAddSection}
              disabled={savingSection}
              className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/80 transition-colors disabled:opacity-50"
            >
              <FiSave size={18} />
              {savingSection ? "Saving..." : "Save Section"}
            </button>
            <button
              onClick={cancelNewSection}
              disabled={savingSection}
              className="flex items-center gap-2 bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
            >
              <IoClose size={20} />
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Saved sections list */}
      <div className="space-y-4">
        {savedSections.length > 0 && (
          <h3 className="text-xl font-semibold text-gray-800 mb-3">Report Sections</h3>
        )}

        {savedSections.map((section) => (
          <div
            key={section.sectionId}
            className="border-2 border-primary/30 rounded-lg p-4 bg-white shadow-sm"
          >
            {editingSectionId === section.sectionId ? (
              <>
                <input
                  type="text"
                  value={section.title}
                  onChange={(e) => updateEditingSection(section.sectionId, "title", e.target.value)}
                  placeholder="Section Title"
                  className="w-full px-3 py-2 border-2 border-primary/30 rounded-lg focus:outline-none focus:border-primary/90 font-semibold mb-3"
                />
                <textarea
                  value={section.content}
                  onChange={(e) => updateEditingSection(section.sectionId, "content", e.target.value)}
                  placeholder="Enter section content..."
                  className="w-full min-h-[120px] p-3 border-2 border-primary/30 rounded-lg focus:outline-none focus:border-primary/90 focus:ring-2 focus:ring-primary transition-all resize-y mb-3"
                />
                <div className="mb-3">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Section Images
                  </label>
                  <ImageUploader
                    value={section.photoIDs}
                    onChange={(photoIDs) => updateEditingSection(section.sectionId, "photoIDs", photoIDs)}
                    multiple={true}
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEditSection(section)}
                    disabled={savingSection}
                    className="flex items-center gap-2 bg-primary text-white px-3 py-2 rounded-lg hover:bg-primary/80 transition-colors disabled:opacity-50"
                  >
                    <FiSave size={18} />
                    {savingSection ? "Saving..." : "Save"}
                  </button>
                  <button
                    onClick={cancelEditSection}
                    disabled={savingSection}
                    className="flex items-center gap-2 bg-gray-500 text-white px-3 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                  >
                    <IoClose size={20} />
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <div className="flex justify-between items-start mb-3">
                <div className="flex-1">
                  <h4 className="text-lg font-semibold text-gray-800">{section.title}</h4>
                  {section.createdAt && (
                    <p className="text-xs text-gray-500 mt-1">
                      Created: {formatDateTime(`${section.createdAt}Z`)}
                    </p>
                  )}
                  <p className="text-gray-700 whitespace-pre-wrap mb-3">{section.content}</p>
                </div>

                {/* Images */}
                {section.photoIDs?.length <= 1 ? (
                  <div className="flex gap-2 items-end mt-3">
                    {section.photoIDs.map((photoId, idx) => (
                      <Image
                        key={idx}
                        width={120}
                        height={120}
                        src={getImg(photoId)}
                        alt={`Section image ${idx + 1}`}
                        className="object-cover rounded border hover:scale-105 transition-transform cursor-pointer"
                        style={{ width: 120, height: 120 }}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 justify-items-center items-end gap-2 mt-4 pt-5">
                    {seeMore ? (
                      section.photoIDs.map((photoId, idx) => (
                        <Image
                          key={idx}
                          width={120}
                          height={120}
                          src={getImg(photoId)}
                          alt={`Section image ${idx + 1}`}
                          className="object-cover rounded border hover:scale-105 transition-transform cursor-pointer"
                          style={{ width: 120, height: 120 }}
                        />
                      ))
                    ) : (
                      <Image
                        width={120}
                        height="auto"
                        src={getImg(section.photoIDs[0])}
                        alt="Section image"
                        className="object-cover rounded border hover:scale-105 transition-transform cursor-pointer"
                      />
                    )}
                    <button
                      onClick={() => setSeeMore((p) => !p)}
                      className="text-blue-600 hover:text-blue-800 transition-colors p-1 rounded hover:bg-blue-50"
                    >
                      {seeMore ? "See Less" : "See More"}
                    </button>
                  </div>
                )}

                <div className="flex gap-2">
                  <button
                    onClick={() => startEditSection(section.sectionId)}
                    className="text-blue-600 hover:text-blue-800 transition-colors p-1 rounded hover:bg-blue-50"
                    title="Edit Section"
                  >
                    <FiEdit2 size={20} />
                  </button>
                  <button
                    onClick={() => handleDeleteSection(section.sectionId)}
                    className="text-red-500 hover:text-red-700 transition-colors p-1 rounded hover:bg-red-50"
                    title="Delete Section"
                  >
                    <IoTrash size={20} />
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}